import React, { useState, useMemo } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

// Ported from the senior's live startup-web (layout/Sidebar/SidebarNavItem.js).
// Adapted for investor-web:
//  - our icons are SVGR components driven by useFill / useStroke, not lucide's size/color
//  - our items carry `title`; startup-web's carry `label`
//  - no mixpanel trackEvent (startup-web logic)
//
// Items without `children` render exactly as they did before this port: no chevron,
// no Collapse, click navigates. Only an item that is given children becomes a parent.
const SidebarNavItem = ({
  icon: Icon,
  title,
  path,
  children,
  permission = true,
  // `disabled` renders the item but makes it inert (Billing & Plan until its pages exist).
  // It is NOT `permission: false`, which SidebarNavigation filters out of the list entirely.
  disabled = false,
  // `onClick` takes over from navigation for items that open a menu instead of a route.
  onClick,
  useFill,
  useStroke,
  drawerToggle,
  collapsed = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const isParent = children && children.length > 0;

  const isPathActive = (itemPath, currentPath) => {
    if (!itemPath) return false;
    if (itemPath === '/') return currentPath === '/';
    return (
      currentPath === itemPath ||
      (currentPath.startsWith(itemPath) && currentPath[itemPath.length] === '/')
    );
  };

  const filteredChildren = useMemo(() => {
    const filtered = children?.filter((child) => child.permission !== false) || [];
    const seen = new Set();
    return filtered.filter((child) => {
      if (seen.has(child.path)) return false;
      seen.add(child.path);
      return true;
    });
  }, [children]);

  const childPaths = useMemo(() => filteredChildren.map((c) => c.path), [filteredChildren]);

  const isActive = useMemo(() => {
    if (path) return isPathActive(path, location.pathname);
    return childPaths.some((cp) => isPathActive(cp, location.pathname));
  }, [location.pathname, path, childPaths]);

  const hasActiveChild = useMemo(
    () => childPaths.some((cp) => isPathActive(cp, location.pathname)),
    [location.pathname, childPaths]
  );

  // Expand on load when a child owns the current route; a collapsed rail never expands.
  React.useEffect(() => {
    if (collapsed) {
      setIsOpen(false);
    } else if (hasActiveChild && !isOpen) {
      setIsOpen(true);
    }
  }, [collapsed, hasActiveChild]);

  const handleClick = (event) => {
    if (!permission || disabled) return;
    if (onClick) {
      onClick(event);
      return;
    }
    if (isParent) {
      if (!collapsed) setIsOpen((o) => !o);
    } else if (path) {
      navigate(path);
      if (drawerToggle) drawerToggle();
    }
  };

  const isInteractive = permission && !disabled;

  const handleChildClick = (child) => {
    if (child.path && child.path !== '#') navigate(child.path);
    if (drawerToggle) drawerToggle();
  };

  const itemIsActive = isActive && !isParent;

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          height: '2.125rem',
          px: collapsed ? '0.5rem' : '0.75rem',
          mx: '0.5rem',
          mb: '2px',
          borderRadius: '6px',
          cursor: isInteractive ? 'pointer' : 'default',
          position: 'relative',
          backgroundColor: itemIsActive ? 'rgba(255,255,255,0.15)' : 'transparent',
          transition: 'background 0.15s ease',
          '&:hover': {
            backgroundColor: isInteractive
              ? itemIsActive
                ? 'rgba(255,255,255,0.18)'
                : 'rgba(255,255,255,0.08)'
              : 'transparent',
          },
          // left accent bar for the active item
          '&::before': itemIsActive
            ? {
                content: '""',
                position: 'absolute',
                left: '-0.5rem',
                top: '25%',
                bottom: '25%',
                width: '3px',
                borderRadius: '0 3px 3px 0',
                backgroundColor: 'rgba(255,255,255,0.9)',
              }
            : {},
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: collapsed ? 0 : '0.625rem',
            flex: collapsed ? 'none' : 1,
            minWidth: 0,
          }}
        >
          {Icon && (
            <Icon
              width={17}
              height={17}
              style={{ flexShrink: 0 }}
              {...(useFill ? { fill: '#FFFFFF' } : {})}
              {...(useStroke ? { stroke: '#FFFFFF' } : {})}
            />
          )}
          {!collapsed && (
            <Typography
              sx={{
                fontSize: '0.9375rem',
                fontWeight: itemIsActive ? 600 : hasActiveChild ? 500 : 400,
                color: 'white',
                lineHeight: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                letterSpacing: '0.01em',
              }}
            >
              {title}
            </Typography>
          )}
        </Box>

        {isParent && !collapsed && (
          <ChevronRight
            size={13}
            color="rgba(255,255,255,0.35)"
            style={{
              flexShrink: 0,
              transition: 'transform 0.2s ease',
              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          />
        )}
      </Box>

      {/* Sub-items */}
      {isParent && !collapsed && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <Box sx={{ position: 'relative', mx: '0.5rem', mb: '4px' }}>
            {/* vertical guide line aligned with icon center */}
            <Box
              sx={{
                position: 'absolute',
                left: '1.3rem',
                top: '0.3rem',
                bottom: '0.3rem',
                width: '1px',
                backgroundColor: 'rgba(255,255,255,0.12)',
              }}
            />

            {filteredChildren.map((child) => {
              const childActive = isPathActive(child.path, location.pathname);
              return (
                <Box
                  key={child.path}
                  onClick={() => handleChildClick(child)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '1.875rem',
                    pl: '2.375rem',
                    pr: '0.625rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: childActive ? 'rgba(255,255,255,0.12)' : 'transparent',
                    transition: 'background 0.15s ease',
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: childActive
                        ? 'rgba(255,255,255,0.15)'
                        : 'rgba(255,255,255,0.06)',
                    },
                    // dot indicator for the active child
                    '&::before': childActive
                      ? {
                          content: '""',
                          position: 'absolute',
                          left: '1.17rem',
                          width: '5px',
                          height: '5px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                        }
                      : {},
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.9375rem',
                      fontWeight: childActive ? 500 : 400,
                      color: childActive ? 'white' : 'rgba(255,255,255,0.72)',
                      lineHeight: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {child.title}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      )}
    </>
  );
};

export default SidebarNavItem;
